import { gql, useMutation, useQuery } from '@apollo/client';
import { Button, Flex, Heading, Text } from 'rebass/styled-components';
import { GET_PATIENT_QUICKVIEW, GET_PRESET_PLAN_COURSES_ANSWERS } from '../../../graphql/queries';
import { IGetPatientMobilePresetPlanCoursesAnswersInput, IGetPatientMobilePresetPlanCoursesAnswersOutput } from '../../../graphql/types/queries/course';
import moment from 'moment';
import { IPatientQuickview, IProviderGetPatientsQuickviewInput, IProviderGetPatientsQuickviewOutput } from '../../../graphql/types/queries/patient';
import { useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers, useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import FormField from '../../../components/FormField';
import { TextInputWithLabel } from '../../../components/TextInput';
import CheckboxInput, { Checkbox } from '../../../components/CheckboxInput';
import Select from 'react-select';
import { ReactSelectStyles } from '../../../styles/ReactSelectStyles';
import { IOkPayload } from '../../../graphql/types/mutations/generics';

const REQUEST_REPLACEMENT = gql`
  mutation hcRequestReplacementKit(
    $patientId: String!
    $firstName: String!
    $lastName: String!
    $address1: String!
    $address2: String
    $zip: String!
    $city: String!
    $state: String!
    $vitD: Boolean
    $rT3: Boolean
    $email: String!
  ) {
    hcRequestReplacementKit(
      patientId:$patientId,
      firstName:$firstName,
      lastName:$lastName,
      address1:$address1,
      address2:$address2,
      zip:$zip,
      city:$city,
      state:$state,
      vitD:$vitD,
      rT3:$rT3,
      email:$email
    ) {
      ok
    }
  }
`;

interface IRequestOutput {
  hcRequestReplacementKit: IOkPayload
}

interface IRequestInput {
  patientId: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  zip: string;
  city: string;
  state: string;
  vitD?: boolean;
  rT3?: boolean;
  email: string;
}

const validationSchema = yup.object({
  email: yup
    .string()
    .required('first name cannot be empty'),
  firstName: yup
    .string()
    .required('first name cannot be empty'),
  lastName: yup
    .string()
    .required('last name cannot be empty'),
  address1: yup
    .string()
    .required('address line 1 cannot be empty'),
  zip: yup
    .string()
    .required('zip cannot be empty'),
  state: yup
    .string()
    .required('state cannot be empty'),
  city: yup
    .string()
    .required('city cannot be empty'),
  vitD: yup
    .boolean(),
  rT3: yup
    .boolean(),
});

const states = [
  { label: 'AL - Alabama', value: 'AL' },
  { label: 'AK - Alaska', value: 'AK' },
  { label: 'AZ - Arizona', value: 'AZ' },
  { label: 'AR - Arkansas', value: 'AR' },
  { label: 'CA - California', value: 'CA' },
  { label: 'CO - Colorado', value: 'CO' },
  { label: 'CT - Connecticut', value: 'CT' },
  { label: 'DE - Delaware', value: 'DE' },
  { label: 'FL - Florida', value: 'FL' },
  { label: 'GA - Georgia', value: 'GA' },
  { label: 'HI - Hawaii', value: 'HI' },
  { label: 'ID - Idaho', value: 'ID' },
  { label: 'IL - Illinois', value: 'IL' },
  { label: 'IN - Indiana', value: 'IN' },
  { label: 'IA - Iowa', value: 'IA' },
  { label: 'KS - Kansas', value: 'KS' },
  { label: 'KY - Kentucky', value: 'KY' },
  { label: 'LA - Louisiana', value: 'LA' },
  { label: 'ME - Maine', value: 'ME' },
  { label: 'MD - Maryland', value: 'MD' },
  { label: 'MA - Massachusetts', value: 'MA' },
  { label: 'MI - Michigan', value: 'MI' },
  { label: 'MN - Minnesota', value: 'MN' },
  { label: 'MS - Mississippi', value: 'MS' },
  { label: 'MO - Missouri', value: 'MO' },
  { label: 'MT - Montana', value: 'MT' },
  { label: 'NE - Nebraska', value: 'NE' },
  { label: 'NV - Nevada', value: 'NV' },
  { label: 'NH - New Hampshire', value: 'NH' },
  { label: 'NJ - New Jersey', value: 'NJ' },
  { label: 'NM - New Mexico', value: 'NM' },
  { label: 'NY - New York', value: 'NY' },
  { label: 'NC - North Carolina', value: 'NC' },
  { label: 'ND - North Dakota', value: 'ND' },
  { label: 'OH - Ohio', value: 'OH' },
  { label: 'OK - Oklahoma', value: 'OK' },
  { label: 'OR - Oregon', value: 'OR' },
  { label: 'PA - Pennsylvania', value: 'PA' },
  { label: 'RI - Rhode Island', value: 'RI' },
  { label: 'SC - South Carolina', value: 'SC' },
  { label: 'SD - South Dakota', value: 'SD' },
  { label: 'TN - Tennessee', value: 'TN' },
  { label: 'TX - Texas', value: 'TX' },
  { label: 'UT - Utah', value: 'UT' },
  { label: 'VT - Vermont', value: 'VT' },
  { label: 'VA - Virginia', value: 'VA' },
  { label: 'WA - Washington', value: 'WA' },
  { label: 'WV - West Virginia', value: 'WV' },
  { label: 'WI - Wisconsin', value: 'WI' },
  { label: 'WY - Wyoming', value: 'WY' }
];

export const RequestReplacementKitInner = ({ data, patientId, close }: { data: IPatientQuickview; patientId: string; close: () => void }) => {
  const toastId = useRef<any>(null);
  const [requestReplace, { loading }] = useMutation<IRequestOutput, IRequestInput>(REQUEST_REPLACEMENT)
  const [ptData, setPtData] = useState<{
    email:string | undefined | null
    firstName: string | undefined | null
    lastName: string | undefined | null
    address1: string | undefined | null
    address2: string | undefined | null
    zip: string | undefined | null
    state: string | undefined | null
    city: string | undefined | null
    vitD: boolean
    rT3: boolean
  }>({
    email:data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    address1: data.address1,
    address2: data.address2,
    zip: data.zip,
    state: data.state,
    city: data.city,
    vitD: false,
    rT3: false
  })

  const _send = async (values: typeof ptData, actions: FormikHelpers<any>) => {

    if (
      !values.firstName ||
      !values.lastName ||
      !values.address1 ||
      !values.zip ||
      !values.state ||
      !values.city
    ) {
      toast("Missing information", { autoClose: 3000, isLoading: true, closeButton: false, type: toast.TYPE.ERROR });
      actions.setSubmitting(false)
      return
    }
    toastId.current = toast("Sending Request for replacement", { autoClose: false, isLoading: true, closeButton: false });

    const res = await requestReplace({
      variables: {
        ...values as any,
        patientId,
        email: data.email
      }
    })

    if (res && res.data && res.data.hcRequestReplacementKit) {
      if (res.data.hcRequestReplacementKit.ok) {
        toast.update(toastId.current, {
          render: 'Kit replacement request sent!',
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
        actions.setSubmitting(false)
        close()
      } else {
        toast.update(toastId.current, {
          render: 'An error occured',
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeButton: null // The closeButton defined on ToastContainer will be used
        });
        actions.setSubmitting(false)
      }
    } else {
      toast.update(toastId.current, {
        render: 'An error occured',
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 5000,
        closeButton: null // The closeButton defined on ToastContainer will be used
      });
      actions.setSubmitting(false)
    }
  }
  return (
    <Formik
      initialValues={ptData}
      isInitialValid={() => {
        try {
          const aa = validationSchema.isValidSync({
            email:data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            address1: data.address1,
            address2: data.address2,
            zip: data.zip,
            state: data.state,
            city: data.city,
            vitD: false,
            rT3: false
          });

          if (!aa) {
            return false;
          }
          return true;
        } catch (err) {
          return false;
        }
      }}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        actions.setSubmitting(true)
        _send(values, actions)
      }}>
      {({ touched, errors, isValid, values, setFieldValue, isSubmitting }) => {
        console.log({ isValid, errors })
        return (
          <>
            <Flex mb={4}>
              <Flex flex={1}>
                <Text fontWeight={300}>Patient ID:</Text>
                <Text fontWeight={600} ml={2}>{patientId}</Text>
              </Flex>
              <Flex >
                <Text fontWeight={300}>Lab:</Text>
                <Text fontWeight={600} ml={2}>{!values.rT3 && !values.vitD ? 'IHD' : 'Ayumetrix'}</Text>
              </Flex>
            </Flex>
            <Form>
              <>
                  <FormField mb={4}>
                    <TextInputWithLabel disabled placeholder="email" name="email" id="inputemail" />
                  </FormField>
                <Flex>
                  <FormField mb={4} error={touched.firstName && errors.firstName}>
                    <TextInputWithLabel placeholder="firstName" name="firstName" id="input_firstName" />
                  </FormField>

                  <FormField mb={4} ml={3} error={touched.lastName && errors.lastName}>
                    <TextInputWithLabel placeholder="lastName" name="lastName" id="input_lastName" />
                  </FormField>
                </Flex>

                <FormField mb={4} error={touched.address1 && errors.address1}>
                  <TextInputWithLabel autoComplete="off" data-lpignore="true" placeholder="address line 1" name="address1" id="input_address1" />
                </FormField>

                <FormField mb={4} error={touched.address2 && errors.address2}>
                  <TextInputWithLabel placeholder="address line 2" name="address2" id="input_address2" />
                </FormField>

                <FormField mb={4} error={touched.city && errors.city}>
                  <TextInputWithLabel placeholder="city" name="city" id="input_city" />
                </FormField>

                <FormField mb={4} error={touched.zip && errors.zip}>
                  <TextInputWithLabel placeholder="zip" name="zip" id="input_zip" />
                </FormField>

                <FormField mb={4} error={touched.state && errors.state}>
                  {/* <TextInputWithLabel placeholder="state" name="state" id="input_state" /> */}
                  <Select
                    isSearchable={true}
                    styles={ReactSelectStyles.initialStyles}
                    options={states}
                    value={states.find(x => x.value === values.state)}
                    onChange={(option: any) => {
                      if (!option) setFieldValue('state', null)
                      setFieldValue('state', option.value)
                    }} />
                </FormField>

                <Flex mb={4}>
                  <Checkbox isChecked={values.vitD} onClick={() => {
                    setFieldValue('vitD', !values.vitD)
                  }} label='Vitamin D' />
                  <Checkbox style={{ marginLeft: '24px' }} isChecked={values.rT3} onClick={() => {
                    setFieldValue('rT3', !values.rT3)
                  }} label='rT3' />
                </Flex>

                <Flex alignItems="center" flexDirection="column">
                  <Button disabled={!isValid || !!isSubmitting} variant="primary" width={1 / 2} type="submit" id="btn_login">
                    {!isSubmitting ? 'Send' : 'Sending...'}
                  </Button>
                </Flex>
              </>
            </Form>
          </>
        );
      }}
    </Formik>
  )
}


export const RequestReplacementKit = ({ patientId, close }: { patientId: string; close: () => void }) => {
  const { data, loading, error } = useQuery<IProviderGetPatientsQuickviewOutput, IProviderGetPatientsQuickviewInput>(GET_PATIENT_QUICKVIEW, {
    variables: {
      id: patientId
    }
  })


  if (!!loading || !data) return null;

  return (
    <Flex flexDirection={'column'} paddingX={'36px'} style={{ overflowY: 'auto' }}>
      <Heading mb={3} textAlign={'center'}>Request Replacement Kit</Heading>

      <RequestReplacementKitInner data={data.getProviderPatientQuickview} patientId={patientId} close={close} />

    </Flex>
  );
};
